/**
 * Layar **pemasangan perangkat** — yang muncul di tablet yang belum punya token.
 *
 * ### ⛔⛔ Kenapa layar ini ada
 * Sampai 2026-08-06 satu-satunya cara menyalakan terminal adalah menempel access token ke
 * `localStorage` lewat DevTools. Itu menuntut orang yang tahu DevTools berdiri di depan tiap tablet
 * — dan di outlet, orang itu tak ada.
 *
 * ⇒ Manajer meminta kode di portal (layar Lantai → perangkat → **Pasangkan**), lalu kodenya diketik
 *   di sini. Sesudah itu tablet mengurus dirinya sendiri: rahasia perangkat menukar token baru tiap
 *   10 jam tanpa ada yang menyentuhnya.
 *
 * ⚠️ Kodenya memuat awalan tenant (`coba-ABCD…`) supaya tablet tahu database mana yang ditanya —
 * ini satu-satunya tempat tenant datang dari ketikan, dan alasannya ada di `pairing.service.ts`:
 * pemasangan justru **perbuatan memperoleh** kredensialnya, jadi belum ada apa pun untuk dipercaya.
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { saveDevicePairing, saveToken } from '../api/session';

export interface PairDeviceProps {
  /** Dipanggil sesudah berhasil — pemanggil yang memutuskan cara memuat ulang layarnya. */
  readonly onPaired: () => void;
}

export function PairDevice({ onPaired }: PairDeviceProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(() => {
    if (code.trim() === '' || busy) return;
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const r = await fetch('/api/devices/pair', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ code: code.trim() }),
        });
        if (!r.ok) throw new Error('gagal');
        const d = (await r.json()) as {
          access_token: string; device_secret: string; deviceCode: string; tenantCode: string;
        };
        /*
         * ⛔ Rahasianya disimpan LEBIH DULU, baru tokennya. Kalau urutannya terbalik dan tablet
         * mati di antaranya, ia punya token 10 jam tanpa cara memperbaruinya — dan besok paginya
         * berhenti bekerja tanpa ada yang tahu sebabnya.
         */
        saveDevicePairing({
          tenantCode: d.tenantCode, deviceCode: d.deviceCode, secret: d.device_secret,
        });
        saveToken(d.access_token);
        onPaired();
      } catch {
        /* ⚠️ Pesan SERAGAM — server pun tak membedakan kode salah, kedaluwarsa, atau sudah dipakai. */
        setError(t('session.pair.failed'));
      } finally {
        setBusy(false);
      }
    })();
  }, [busy, code, onPaired, t]);

  return (
    <section className="pin-login" aria-label={t('session.pair.title')} data-testid="pair-device">
      <header className="pin-brand">
        <div className="pin-brand-name">{t('session.pair.title')}</div>
        <p className="pin-outlet">{t('session.pair.hint')}</p>
      </header>

      <div className="pin-body">
        <div className="pin-fields">
          <label>
            {t('session.pair.code')}
            <input
              data-testid="pair-code"
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(null); }}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="tenant-XXXXXXXXXXXX"
            />
          </label>
          {error !== null && <p className="error" data-testid="pair-error">{error}</p>}
          <button
            type="button"
            className="action-primary"
            data-testid="pair-submit"
            disabled={code.trim() === '' || busy}
            onClick={submit}
          >
            {busy ? '…' : t('session.pair.button')}
          </button>
        </div>
      </div>

      <footer className="pin-foot"><span className="pin-osbpos">osbpos</span></footer>
    </section>
  );
}
